import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

// Handles file upload button behavior
// Replaces: app/assets/javascripts/uploaders.js
export default class extends Controller {
  static targets = ["link", "input", "form"]
  static values = {
    uploadingText: String,
    uploadText: String
  }

  connect() {
    // Track if we're currently uploading to prevent duplicate submissions
    this.isUploading = false
    // Track the last selected file to prevent duplicate processing
    this.lastSelectedFile = null

    // Listen for Turbo form submission events to reset state
    if (this.hasFormTarget) {
      this.formTarget.addEventListener('turbo:submit-start', this.handleSubmitStart.bind(this))
      this.formTarget.addEventListener('turbo:submit-end', this.handleSubmitEnd.bind(this))
    }
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener('turbo:submit-start', this.handleSubmitStart.bind(this))
      this.formTarget.removeEventListener('turbo:submit-end', this.handleSubmitEnd.bind(this))
    }
  }

  // When upload link is clicked, trigger file input
  triggerUpload(event) {
    event.preventDefault()

    if (this.hasInputTarget && !this.isUploading) {
      this.inputTarget.click()
    }
  }

  // When file is selected, submit the form
  fileSelected(event) {
    const input = event.target

    // Prevent multiple submissions
    if (this.isUploading) {
      return
    }

    if (!input.value) {
      return
    }

    // Prevent processing the same file selection twice
    // (can happen if change event fires multiple times)
    // But only if we actually processed this file before
    // Reset lastSelectedFile check after form submission completes
    if (this.lastSelectedFile === input.value) {
      return
    }

    // Don't prevent default - we need the file to be included in form submission
    // Just stop propagation to prevent other handlers
    event.stopPropagation()

    // Track the selected file BEFORE marking as uploading
    // This prevents the same file from being processed twice if change event fires multiple times
    this.lastSelectedFile = input.value

    // Mark as uploading
    this.isUploading = true

    // Update link text to "uploading..."
    if (this.hasLinkTarget && this.hasUploadingTextValue) {
      this.linkTarget.innerHTML = this.uploadingTextValue
    }

    // Disable link during upload (but NOT the input - disabled inputs don't submit!)
    if (this.hasLinkTarget) {
      this.linkTarget.style.pointerEvents = 'none'
      this.linkTarget.classList.add('disabled')
    }

    // DO NOT disable the input - disabled form fields are not submitted!
    // Instead, we'll prevent clicks on the link while uploading

    // Submit form (Turbo-compatible)
    if (this.hasFormTarget) {
      this.formTarget.requestSubmit()
    } else {
      logger.error('Uploader: No form target found, cannot submit')
      // Reset state if form submission fails
      this.isUploading = false
      this.lastSelectedFile = null
      if (this.hasLinkTarget && this.hasUploadTextValue) {
        this.linkTarget.innerHTML = this.uploadTextValue
      }
      if (this.hasLinkTarget) {
        this.linkTarget.style.pointerEvents = ''
        this.linkTarget.classList.remove('disabled')
      }
    }
  }

  handleSubmitStart(event) {
    // Form submission started - keep uploading state
    this.isUploading = true
  }

  handleSubmitEnd(event) {
    // Check if the form submission was successful
    // Turbo sets detail.success to false if there were HTTP errors
    // But validation errors might still return HTTP 200, so we need to check the response
    const fetchResponse = event.detail?.fetchResponse
    const httpSuccess = event.detail?.success !== false
    const statusOk = fetchResponse?.status && fetchResponse.status >= 200 && fetchResponse.status < 400

    // For now, assume success if HTTP succeeded
    // If there are validation errors, the Turbo Stream will update the DOM with errors
    // and the form will remain, so we'll reset the button text as a fallback
    const wasSuccessful = httpSuccess && statusOk

    // Dispatch event for tracking (apps can listen for this)
    if (wasSuccessful) {
      this.element.dispatchEvent(new CustomEvent('uploader:upload-complete', {
        bubbles: true,
        detail: { success: true }
      }))
    }

    // Reset state immediately - don't wait
    // This allows the user to upload again right away
    this.isUploading = false
    this.lastSelectedFile = null

    // Re-enable link immediately
    if (this.hasLinkTarget) {
      this.linkTarget.style.pointerEvents = ''
      this.linkTarget.classList.remove('disabled')
    }

    // Clear input value immediately after submission completes
    // This allows selecting a new file right away
    // Clear regardless of success/failure - if there were errors, user can select again
    if (this.hasInputTarget) {
      // Clear input value immediately to allow new file selection
      this.inputTarget.value = ''
    }

    // Reset button text after a short delay
    // If the upload was successful, Turbo Stream will replace the form anyway
    // If there were errors, the form stays and we need to reset the button text
    if (this.hasLinkTarget && this.hasUploadTextValue) {
      // Use a timeout to allow Turbo Stream to process first
      // If the form gets replaced (success), this won't matter
      // If the form stays (errors), we'll reset the button text
      setTimeout(() => {
        // Check if the link still exists (form wasn't replaced)
        if (this.hasLinkTarget && this.linkTarget.isConnected) {
          // Check if button still shows uploading text
          if (this.linkTarget.innerHTML === this.uploadingTextValue) {
            // Reset to original upload text
            this.linkTarget.innerHTML = this.uploadTextValue
          }
        }
      }, 500)
    }
  }
}
